import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Table, Tag, Button, Modal, Form, Input, DatePicker, Select, InputNumber, message, Popconfirm, Space } from "antd";
import { usePQQList, usePQQCreate, usePQQUpdate, usePQQDelete } from "../../../api-hooks/pqq";
import dayjs from "dayjs";

const STATUS_COLOR: Record<string, string> = {
  in_progress: "blue", submitted: "orange", passed: "green", failed: "red", withdrawn: "default",
};

export default function PQQPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [messageApi, ctx] = message.useMessage();

  const { data: resp, isLoading } = usePQQList(cid, { page, limit: 25, status: statusFilter });
  const rows = resp?.data ?? [];
  const total = resp?.total ?? 0;

  const create = usePQQCreate({ onSuccess: () => { messageApi.success("Created"); setEditing(null); }, onError: () => messageApi.error("Failed") });
  const update = usePQQUpdate({ onSuccess: () => { messageApi.success("Saved"); setEditing(null); }, onError: () => messageApi.error("Failed") });
  const del = usePQQDelete({ onSuccess: () => messageApi.success("Deleted"), onError: () => messageApi.error("Failed") });

  const openEdit = (row: any) => {
    setEditing(row);
    form.setFieldsValue({ ...row, deadline: row.deadline ? dayjs(row.deadline) : null });
  };

  const openCreate = () => { setEditing({}); form.resetFields(); };

  const handleSave = (vals: any) => {
    const body = { ...vals, deadline: vals.deadline ? vals.deadline.format("YYYY-MM-DD") : null };
    if (editing?.id) update.mutate({ cid, id: editing.id, data: body });
    else create.mutate({ cid, data: body });
  };

  const columns = [
    { title: "Title", dataIndex: "title", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={STATUS_COLOR[v] || "default"}>{v.replace(/_/g, " ")}</Tag> },
    { title: "Deadline", dataIndex: "deadline", render: (v: string) => v ? dayjs(v).format("DD MMM YYYY") : "—" },
    { title: "Score", dataIndex: "score", render: (v: any) => v != null ? `${v}%` : "—" },
    {
      title: "Actions", key: "actions", render: (_: any, row: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
          <Popconfirm title="Delete this submission?" onConfirm={() => del.mutate({ cid, id: row.id })}>
            <Button size="small" danger type="text">Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">PQQ & Procurement</h2>
          <p className="text-slate-500 text-sm mt-0.5">Company #{cid} — Pre-Qualification Questionnaires</p>
        </div>
        <Button type="primary" onClick={openCreate}>New PQQ</Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4" bodyStyle={{ paddingBottom: 0 }}>
        <div className="flex gap-3 mb-4">
          <Select allowClear placeholder="Filter status" value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}
            options={["in_progress","submitted","passed","failed","withdrawn"].map(s => ({ value: s, label: s.replace(/_/g, " ") }))} />
        </div>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ current: page, pageSize: 25, total, onChange: setPage, showSizeChanger: false }}
        />
      </Card>

      <Modal title={editing?.id ? "Edit PQQ" : "New PQQ"} open={!!editing} onCancel={() => setEditing(null)}
        onOk={() => form.validateFields().then(handleSave)} okText="Save"
        confirmLoading={create.isPending || update.isPending} width={560}>
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="status" label="Status" initialValue="in_progress">
              <Select options={["in_progress","submitted","passed","failed","withdrawn"].map(s => ({ value: s, label: s.replace(/_/g, " ") }))} />
            </Form.Item>
            <Form.Item name="deadline" label="Deadline">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="score" label="Score (%)">
              <InputNumber min={0} max={100} style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <Form.Item name="document_url" label="Document URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
